import React, { useMemo } from "react";
import { obtenerPartesFechaHora, pad2 } from "../../../dominio/cita";

const formatMontoInput = (value) => {
  const limpio = String(value || "").replace(/\D/g, "");
  if (!limpio) return "";

  return limpio.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

export default function CitaFormModal({
  show,
  editMode = false,
  form,
  pacientes,
  tiposCita,
  pacienteBusqueda,
  onPacienteChange,
  onChange,
  onFechaHoraChange,
  onCostoChange,
  onSubmit,
  onClose,
  getEtiquetaPaciente
}) {
  const partesFecha = obtenerPartesFechaHora(form.Fecha);
  const horasDisponibles = useMemo(() => Array.from({ length: 12 }, (_, index) => pad2(index + 1)), []);
  const minutosDisponibles = useMemo(() => Array.from({ length: 60 }, (_, index) => pad2(index)), []);

  if (!show) return null;

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{editMode ? "Editar Cita" : "Nueva Cita"}</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            <p className="text-muted mb-3">
              {editMode
                ? "Edita los datos de la cita y guarda para actualizar los cambios."
                : "Completa el formulario para agendar una nueva cita."}
            </p>
            <form onSubmit={onSubmit} className="row g-3">
              <div className="col-12 col-md-6">
                <label className="form-label">Paciente</label>
                <input
                  type="text"
                  className="form-control"
                  list="pacientes-lista"
                  placeholder="Escribe el nombre del paciente"
                  value={pacienteBusqueda}
                  onChange={onPacienteChange}
                  required
                />
                <datalist id="pacientes-lista">
                  {pacientes.map((p) => (
                    <option key={p.IdPaciente} value={getEtiquetaPaciente(p)} />
                  ))}
                </datalist>
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label">Tipo de Cita</label>
                <select name="IdTiposCita" className="form-select" value={form.IdTiposCita} onChange={onChange} required>
                  <option value="">-- Seleccione tipo --</option>
                  {tiposCita.map((t) => (
                    <option key={t.IdTiposCita} value={t.IdTiposCita}>{t.Nombre}</option>
                  ))}
                </select>
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label">Fecha</label>
                <div className="appointment-datetime-picker">
                  <input
                    type="date"
                    className="form-control"
                    value={partesFecha.date}
                    onChange={(e) => onFechaHoraChange("date", e.target.value)}
                    required
                  />
                  <div className="appointment-time-row">
                    <select
                      className="form-select"
                      aria-label="Hora"
                      value={partesFecha.hour12}
                      onChange={(e) => onFechaHoraChange("hour12", e.target.value)}
                      required
                    >
                      {horasDisponibles.map((hora) => (
                        <option key={hora} value={hora}>{hora}</option>
                      ))}
                    </select>
                    <select
                      className="form-select"
                      aria-label="Minutos"
                      value={partesFecha.minute}
                      onChange={(e) => onFechaHoraChange("minute", e.target.value)}
                      required
                    >
                      {minutosDisponibles.map((minuto) => (
                        <option key={minuto} value={minuto}>{minuto}</option>
                      ))}
                    </select>
                    <div className="btn-group appointment-meridiem" role="group" aria-label="Seleccionar AM o PM">
                      <input
                        type="radio"
                        className="btn-check"
                        name="FechaMeridiem"
                        id="fecha-am"
                        value="AM"
                        checked={partesFecha.meridiem === "AM"}
                        onChange={(e) => onFechaHoraChange("meridiem", e.target.value)}
                      />
                      <label className="btn btn-outline-primary" htmlFor="fecha-am">a. m.</label>
                      <input
                        type="radio"
                        className="btn-check"
                        name="FechaMeridiem"
                        id="fecha-pm"
                        value="PM"
                        checked={partesFecha.meridiem === "PM"}
                        onChange={(e) => onFechaHoraChange("meridiem", e.target.value)}
                      />
                      <label className="btn btn-outline-primary" htmlFor="fecha-pm">p. m.</label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label">Costo</label>
                <div className="input-group">
                  <span className="input-group-text">&#8353;</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    name="Costo"
                    className="form-control"
                    placeholder="0"
                    value={formatMontoInput(form.Costo)}
                    onChange={onCostoChange}
                    required
                  />
                </div>
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label">Descuento</label>
                <input
                  type="text"
                  className="form-control"
                  value={`${form.Descuento}%`}
                  disabled
                />
              </div>
              <div className="col-12 col-md-6 d-flex align-items-center">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="chkIVA"
                    name="IVA"
                    checked={Number(form.IVA) === 4}
                    onChange={onChange}
                  />
                  <label className="form-check-label" htmlFor="chkIVA">
                    Aplicar IVA (4%)
                  </label>
                </div>
              </div>
              <div className="col-12">
                <div className="alert alert-info py-2">
                  <div className="d-flex flex-column flex-sm-row justify-content-between gap-2 mb-1">
                    <span><strong>Descuento:</strong> {form.Descuento}%</span>
                    <span><strong>IVA:</strong> {Number(form.IVA) === 4 ? "4%" : "0%"}</span>
                  </div>
                  <small className="text-muted">El descuento se completa al seleccionar el tipo de cita. Marca IVA si necesitas aplicar el 4%.</small>
                </div>
              </div>
              <div className="col-12 d-flex flex-column flex-sm-row gap-2">
                <button type="submit" className="btn btn-success btn-sm w-100 w-sm-auto">
                  {editMode ? "Actualizar cita" : "Agregar cita"}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm w-100 w-sm-auto"
                  onClick={onClose}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
